import { DefaultButton, Icon, INavLink, INavLinkGroup, Nav, Stack } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../src/taskpane/components/Loader";
import { RootState } from "../../../src/taskpane/components/_v2/redux/storeMain";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { IMailFolder } from "../../../src/taskpane/interfaces/IMailFolder";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { IWellKnownFolder } from "../../../src/taskpane/interfaces/IWellKnownFolder";
import { setContentManagerExchange, setContentManagerExchangeAdditionalMailboxes } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { setSearch } from "../../../src/taskpane/redux/modules/search";
import { addWorkingDirectories, setWorkingDirectories } from "../../../src/taskpane/redux/modules/workingDirectory";
import { getExchangeFolders, getWellKnownFolders } from "../../../src/taskpane/services/ExchangeService";
import generalModule from "../../../utilities/GeneralModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { useMailboxPopUpAuth } from "../../Hooks/useMailboxPopUpAuth";
import { apiGetAdditionalMailboxes } from "../../../src/taskpane/services/ApiService";

interface IExchangeDirectoryProps {

}

interface IFolderNavLink extends INavLink {
    id: string;
    mailboxId: string;
}

interface IMailboxInfo {
    index: number;
    mailboxAddress: string;
    folders: IMailFolder[];
    wellknownFolders?: IWellKnownFolder[];
}

const ExchangeDirectory = ({ }: IExchangeDirectoryProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const account = useSelector((state: RootState) => state.user?.account);
    const exchange = useSelector((state: RootState) => state.contentManagerDirectory?.exchange);
    const contentManagerFilterValue = useSelector((state: RootState) => state.contentManagerDirectory?.filterValue);
    const searchValue = useSelector((state: RootState) => state.search?.value)?.value;
    const additionalMailboxes = useSelector((state: RootState) => state.contentManagerDirectory?.exchange?.additionalMailboxes);

    const { isMailboxTokenAvailable, isMailboxTokenRequestInProgress, requestMailboxToken } = useMailboxPopUpAuth();

    const dispatch = useDispatch();

    const [navLinkGroups, setNavLinkGroups] = React.useState<INavLinkGroup[]>([]);
    const [wellKnownFolders, setWellKnownFolders] = React.useState<IWellKnownFolder[]>([]);
    const [isLoadingFolders, setIsLoadingFolders] = React.useState(true);

    const isFirstLoad = React.useRef(true);

    React.useEffect(() => {
        if (isMailboxTokenAvailable && account) {
            (async () => {
                setIsLoadingFolders(true);
                if (additionalMailboxes === undefined) {
                    try {
                        const mailboxes = await apiGetAdditionalMailboxes(account.accountId);
                        dispatch(setContentManagerExchangeAdditionalMailboxes(mailboxes?.value?.filter(x => x?.length > 0)));
                    } catch (error) {
                        logger(error, "ExchangeDirectory.tsx (getAdditionalMailboxes)");
                    }
                }
            })();
        } else {
            setIsLoadingFolders(false);
        }
    }, [isMailboxTokenAvailable, additionalMailboxes, account]);

    React.useEffect(() => {
        if (additionalMailboxes !== undefined) {
            (async () => {
                const navLinkGroups: INavLinkGroup[] = [];
                let wellknownList: IWellKnownFolder[] = [];

                const allMailboxes = [account.username, ...additionalMailboxes];

                let mailboxInfoList: IMailboxInfo[] = await Promise.all(
                    allMailboxes.map(async (mailbox, index) => {
                        const [folders, wellknown] = await Promise.all([
                            fetchFolders(mailbox),
                            getWellKnownFolders(mailbox)
                        ]);

                        return {
                            index: index,
                            mailboxAddress: mailbox as string,
                            folders: folders,
                            wellknownFolders: wellknown
                        } as IMailboxInfo;
                    })
                );

                if (mailboxInfoList?.length > 0) {
                    mailboxInfoList = mailboxInfoList
                        .sort((a, b) => a.index - b.index)
                        .filter(mailboxInfo => mailboxInfo.folders?.length > 0);

                    for (let index = 0; index < mailboxInfoList.length; index++) {
                        const mailboxInfo: IMailboxInfo = mailboxInfoList[index];

                        if (mailboxInfo.index === 0) {
                            const inboxWellKnown = mailboxInfo.wellknownFolders?.find(x => x.name === "inbox");
                            if (inboxWellKnown && generalModule.isInConentMangerMode()) {
                                const inboxFolder = mailboxInfo.folders?.find(x => x.id === inboxWellKnown.id);
                                if (inboxFolder) {
                                    dispatch(setContentManagerExchange({ selectedFolderId: inboxFolder.id, selectedFolderName: inboxFolder.displayName, selectedAccount: mailboxInfo.mailboxAddress }));

                                    const itemToAdd: IPageSettings = {
                                        type: ComponentTypes.Exchange,
                                        siteId: "",
                                        siteDisplayName: inboxFolder.displayName,
                                        webUrl: "",
                                        directory: "",
                                        DocumentLibrary: "",
                                        displayName: inboxFolder.displayName,
                                        exchangeFolderId: inboxFolder.id
                                    };
                                    dispatch(setWorkingDirectories([itemToAdd]));
                                }
                            }
                        }

                        if (mailboxInfo.wellknownFolders) {
                            wellknownList = wellknownList.concat(mailboxInfo.wellknownFolders);
                        }

                        navLinkGroups.push(createNavBarGroup(mailboxInfo.mailboxAddress, mailboxInfo.folders));
                    }
                }

                setWellKnownFolders(wellknownList);
                setIsLoadingFolders(false);
                setNavLinkGroups(navLinkGroups);
                isFirstLoad.current = false;
            })();
        }
    }, [additionalMailboxes])

    React.useEffect(() => {
        if (!isFirstLoad.current && isMailboxTokenAvailable) {
            (async () => {
                const navLinkGroups: INavLinkGroup[] = [];
                setIsLoadingFolders(true);

                let allMailboxes = [account.username];
                if (additionalMailboxes?.length > 0) {
                    allMailboxes = allMailboxes.concat(additionalMailboxes);
                }

                let mailboxInfoList: IMailboxInfo[] = await Promise.all(
                    allMailboxes.map(async (mailbox, index) => {
                        return {
                            index: index,
                            mailboxAddress: mailbox as string,
                            folders: await fetchFolders(mailbox)
                        } as IMailboxInfo;
                    })
                );

                if (mailboxInfoList?.length > 0) {
                    mailboxInfoList = mailboxInfoList
                        .sort((a, b) => a.index - b.index)
                        .filter(mailboxInfo => mailboxInfo.folders?.length > 0);

                    for (let index = 0; index < mailboxInfoList.length; index++) {
                        const mailboxInfo: IMailboxInfo = mailboxInfoList[index];
                        navLinkGroups.push(createNavBarGroup(mailboxInfo.mailboxAddress, mailboxInfo.folders));
                    }
                }

                setNavLinkGroups(navLinkGroups);
                setIsLoadingFolders(false);
            })();
        }
    }, [contentManagerFilterValue, searchValue, isMailboxTokenAvailable, additionalMailboxes]);

    const fetchFolders = async (accountUsername: string) => {
        try {
            let filter = "";
            if (generalModule.isInConentMangerMode()) {
                filter = contentManagerFilterValue ? contentManagerFilterValue : "";
            } else {
                filter = searchValue ? searchValue : "";
            }

            return await getExchangeFolders(accountUsername, filter);
        } catch (error) {
            logger(error, "ExchangeDirectory.tsx (fetchFolders)");
            return [];
        }
    }

    const createNavBarGroup = (accountName: string, folders: IMailFolder[]) => {
        const links = createNavFromFolders(folders, accountName);
        return {
            name: accountName,
            links
        };
    }

    const createNavFromFolders = (folders: IMailFolder[], accountName: string): IFolderNavLink[] => {
        const links: IFolderNavLink[] = [];

        if (folders.length > 0) {
            for (let index = 0; index < folders.length; index++) {
                const folder = folders[index];
                const newFolderLink: IFolderNavLink = {
                    id: folder.id,
                    mailboxId: accountName,
                    key: folder.id,
                    name: folder.displayName,
                    url: "#",
                    links: folder.childFolders ? createNavFromFolders(folder.childFolders, accountName) : []
                };

                links.push(newFolderLink);
            }
        }

        return links;
    }

    const _onLinkClick = (ev?: React.MouseEvent<HTMLElement, MouseEvent>, item?: IFolderNavLink) => {
        const itemToAdd: IPageSettings = {
            type: ComponentTypes.Exchange,
            siteId: "",
            siteDisplayName: item.name,
            webUrl: "",
            directory: "",
            DocumentLibrary: "",
            displayName: item.name,
            exchangeFolderId: item.id
        };

        if (generalModule.isInConentMangerMode()) {
            dispatch(setWorkingDirectories([itemToAdd]));
        } else {
            dispatch(addWorkingDirectories([itemToAdd]));
        }

        dispatch(setContentManagerExchange({ selectedFolderId: item.id, selectedFolderName: item.name, selectedAccount: item.mailboxId }));

        if (searchValue) {
            dispatch(setSearch(""));
        }

        ev.preventDefault();
        ev.stopPropagation();
    }

    return (
        <>
            {!isMailboxTokenAvailable && !isMailboxTokenRequestInProgress &&
                <div style={{ textAlign: "center", paddingTop: "15px" }}>
                    <DefaultButton onClick={requestMailboxToken} text={languageStrings.RequestAccess} />
                </div>
            }
            {(isLoadingFolders || isMailboxTokenRequestInProgress) && <Loader label={languageStrings.Loading} />}
            {isMailboxTokenAvailable && !isMailboxTokenRequestInProgress && !isLoadingFolders && <Nav
                selectedKey={exchange.selectedFolderId}
                ariaLabel="Nav basic example"
                groups={navLinkGroups}
                onLinkClick={_onLinkClick}
                onRenderLink={(link: IFolderNavLink) => {
                    const wellKnownFolder = wellKnownFolders.find(x => x.id === link.id);
                    const IconName = wellKnownFolder ? wellKnownFolder.icon : "FabricFolder";
                    return (
                        <div>
                            <Stack horizontal tokens={{ childrenGap: 5 }}>
                                <Icon iconName={IconName} />
                                <span style={{ paddingLeft: "5px" }}>{link.name}</span>
                            </Stack>
                        </div>
                    );
                }}
            />}
        </>
    )
}

export default React.memo(ExchangeDirectory);